/**
 * RoomUnit Component
 * Renders a single room with radiator, thermostat, and piping
 */

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { PredefinedValvePlusThermostatValve } from "../../Shared/PredefinedValvePlusThermostatValve";
import { ROOM_WIDTH, ROOM_HEIGHT, COLORS, D_WATER } from "./constants";
import { formatColorAlpha } from "./utils";

// Import images
import radiator3Start from "./radiator 3 start.png";
import radiator3Middle from "./radiator 3 middle.png";
import radiator3End from "./radiator 3 end.png";
import thermostatIcon from "./thermostatRadiator.png";
import flowrateIcon from "./flowrate.png";

export const RoomUnit = ({
  x,
  y,
  roomTemperature = 15,
  targetTemperature = 20,
  radiatorCount = 5,
  thermostatOpening = 1,
  massFlowRate = 0,
  valvePosition = 0.5,
  onValveChange,
  onTemperatureChange,
  onShowHighlights,
  onStopAnimation,
  showHighlights = false,
  isRunning = false,
  showValveThermostat = true,
  showRadiatorArea = true,
}) => {
  const { t } = useTranslation();
  // Calculate visual parameters
  const colorStrength = useMemo(() => {
    return 0.2 + (thermostatOpening || 0) * 0.3;
  }, [thermostatOpening]);

  const radiatorWidth = useMemo(() => {
    return {
      start: 14.5,
      middle: 7,
      end: 14.5,
      total: 14.5 + 7 * radiatorCount + 14.5,
    };
  }, [radiatorCount]);

  const flowRateLPM = useMemo(() => {
    // Convert kg/s to L/min:
    // 1. kg/s → m³/s: divide by density (kg/m³)
    // 2. m³/s → L/min: multiply by 1000 (L/m³) * 60 (s/min)
    return ((massFlowRate / D_WATER) * 60000).toFixed(1);
  }, [massFlowRate]);

  const thermostatHeight = useMemo(() => {
    return thermostatOpening * 15;
  }, [thermostatOpening]);

  // Flow animation: duration inversely proportional to flow rate
  const flowAnimationDuration = useMemo(() => {
    if (!isRunning) return 0;
    const rate = parseFloat(flowRateLPM);
    if (rate <= 0.05) return 0;
    // Higher flow rate → shorter duration → faster arrows
    return Math.max(0.4, 18 / rate);
  }, [flowRateLPM, isRunning]);

  // Hot water supply flow path (from bottom-right toward radiator)
  const hotWaterFlowPath = useMemo(() => {
    return `M 202,363 L 202,324 L 165,324 L 165,311 L ${62 + 7 * radiatorCount},311`;
  }, [radiatorCount]);

  // Return water flow path (from radiator back down to system)
  const returnWaterFlowPath = useMemo(() => {
    const radX = 62 + 7 * radiatorCount;
    return `M ${radX},322 L ${radX + 22},322 L ${radX + 22},350`;
  }, [radiatorCount]);

  const flowArrowCount = 5;
  const returnArrowCount = 5;

  return (
    <svg
      key="room-unit"
      x={x}
      y={y}
      width={ROOM_WIDTH}
      height={ROOM_HEIGHT}
      className="room-visualization"
      data-testid="room-visualization"
      onMouseLeave={() => {
        if (onShowHighlights) onShowHighlights(false);
        if (onStopAnimation) onStopAnimation(false);
      }}
    >
      {/* Room Temperature Display */}
      <g id="temperature-display" className="room-temperature-display" data-testid="room-temp">
        <Tooltip 
          title={t('heatPumpSimulation.room.currentDesired')}
          color="white"
        >
          <rect 
            x={ROOM_WIDTH - 90} 
            y={9} 
            width={80} 
            height={40} 
            fill="transparent" 
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
        <text x={ROOM_WIDTH - 70} y={29} fill="#dc3545" fontSize={17} style={{ pointerEvents: 'none' }}>
          {roomTemperature.toFixed(1)}
        </text>
        <text x={ROOM_WIDTH - 61} y={42} fill="#6c757d" fontSize={12} style={{ pointerEvents: 'none' }}>
          {targetTemperature.toFixed(1)}
        </text>
        <text x={ROOM_WIDTH - 35} y={29} fill="#dc3545" fontSize={17} style={{ pointerEvents: 'none' }}>
          {" "}
          °C
        </text>
        <text x={ROOM_WIDTH - 30} y={42} fill="#6c757d" fontSize={12} style={{ pointerEvents: 'none' }}>
          {" "}
          °C
        </text>
      </g>

      {showRadiatorArea && (
        <g data-testid="room-radiator-area">
          {/* Radiator Assembly */}
          <g id="radiator" transform={`translate(${ROOM_WIDTH * 0.07}, ${ROOM_HEIGHT * 0.8325})`}>
            <image href={radiator3Start} x={0} y={0} width={radiatorWidth.start} />

            {Array.from({ length: radiatorCount }, (_, i) => (
              <image
                key={`radiator-section-${i}`}
                href={radiator3Middle}
                x={radiatorWidth.start + radiatorWidth.middle * i}
                y={0}
                width={radiatorWidth.middle}
              />
            ))}

            <image
              href={radiator3End}
              x={radiatorWidth.start + radiatorWidth.middle * radiatorCount}
              y={0}
              width={radiatorWidth.end}
            />
          </g>

          {showRadiatorArea && (
            <g id="thermostat">
              <image 
                href={thermostatIcon} 
                width={25} 
                x={150} 
                y={ROOM_HEIGHT * 0.8} 
              />
             
              {/* Thermostat bar indicator */}
              <rect
                x={178}
                y={300 - thermostatHeight}
                height={thermostatHeight}
                width={5}
                fill="rgba(255, 165, 0, 0.5)"
                stroke="transparent"
              />
              <rect x={177} y={284} height={17} width={7} fill="transparent" stroke="rgba(255, 165, 0, 1)" />
              <line
                x1={178}
                x2={183}
                y1={300 - thermostatHeight}
                y2={300 - thermostatHeight}
                stroke="orange"
                strokeWidth={2}
              />
            </g>
          )}

          {/* Piping - Hot water supply */}
          <g id="hot-water-pipes">
            <rect
              x={167}
              y={322}
              height={4}
              width={33}
              fill={formatColorAlpha(COLORS.hotWater, colorStrength)}
              stroke="transparent"
            />
            <rect
              x={163}
              y={319}
              height={7}
              width={4}
              fill={formatColorAlpha(COLORS.hotWater, colorStrength)}
              stroke="transparent"
            />
            <rect
              x={62 + 7 * radiatorCount}
              y={309}
              height={4}
              width={89 - 7 * radiatorCount}
              fill={formatColorAlpha(COLORS.hotWater, colorStrength)}
              stroke="transparent"
            />
            <rect
              x={200}
              y={322}
              height={41}
              width={4}
              fill={formatColorAlpha(COLORS.hotWater, colorStrength)}
              stroke="transparent"
            />
          </g>

          {/* Piping - Return water */}
          <g id="return-water-pipes">
            <rect
              x={62 + 7 * radiatorCount}
              y={320}
              height={4}
              width={20}
              fill={formatColorAlpha(COLORS.coldWater, colorStrength)}
              stroke="transparent"
            />
            <rect
              x={82 + 7 * radiatorCount}
              y={320}
              height={30}
              width={4}
              fill={formatColorAlpha(COLORS.coldWater, colorStrength)}
              stroke="transparent"
            />
          </g>
        </g>
      )}

      {/* Animated Flow Bubbles - Hot Water Supply */}
      {showRadiatorArea && flowAnimationDuration > 0 && (
        <g id="hot-water-flow-bubbles">
          <defs>
            <radialGradient id="bubbleGradientHot" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="40%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="100%" stopColor="rgba(255,200,200,0.3)" />
            </radialGradient>
          </defs>
          {Array.from({ length: flowArrowCount }, (_, i) => {
            const offset = i / flowArrowCount;
            // Add small randomness: ±15% variation per bubble
            const randomFactor = 1 + (((i * 7 + 3) % 11) - 5) * 0.03;
            const duration = flowAnimationDuration * randomFactor;
            const radiusRandom = 1.2 + (((i * 5 + 2) % 7) * 0.1);
            return (
              <circle
                key={`hot-bubble-${i}`}
                r={radiusRandom}
                fill="url(#bubbleGradientHot)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={0.2}
              >
                <animateMotion
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`-${offset * flowAnimationDuration}s`}
                  path={hotWaterFlowPath}
                  calcMode="linear"
                />
              </circle>
            );
          })}
        </g>
      )}

      {/* Animated Flow Bubbles - Return Water */}
      {showRadiatorArea && flowAnimationDuration > 0 && (
        <g id="return-water-flow-bubbles">
          <defs>
            <radialGradient id="bubbleGradientCold" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="40%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="100%" stopColor="rgba(180,210,230,0.3)" />
            </radialGradient>
          </defs>
          {Array.from({ length: returnArrowCount }, (_, i) => {
            const offset = i / returnArrowCount;
            // Add small randomness: ±15% variation per bubble
            const randomFactor = 1 + (((i * 11 + 7) % 9) - 4) * 0.035;
            const duration = flowAnimationDuration * 0.5 * randomFactor;
            const radiusRandom = 1.0 + (((i * 3 + 1) % 5) * 0.1);
            return (
              <circle
                key={`return-bubble-${i}`}
                r={radiusRandom}
                fill="url(#bubbleGradientCold)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={0.2}
              >
                <animateMotion
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                  begin={`-${offset * flowAnimationDuration * 0.5}s`}
                  path={returnWaterFlowPath}
                  calcMode="linear"
                />
              </circle>
            );
          })}
        </g>
      )}

      {showRadiatorArea && showValveThermostat && (
        <g data-testid="room-valve-thermostat-area" className="predefined-valve-thermostat">
          <g id="flow-rate-indicator" data-testid="room-flow-rate-area">
            <image href={flowrateIcon} width={17} x={48} y={168} />
            <text
              x={68}
              y={179}
              fill={"black"}
              fontSize={12}
            >
              {flowRateLPM}
            </text>
            <text x={68} y={189} fill="#6c757d" fontSize={10}>
              L/min
            </text>
            {showHighlights && (
              <>
                <text x={40} y={220} fill="#6c757d" fontSize={12}>
                  {t('heatPumpSimulation.room.refrigerantFlowRate').split(' ')[0]}
                </text>
                <text x={46} y={235} fill="#6c757d" fontSize={12}>
                  {t('heatPumpSimulation.room.refrigerantFlowRate').split(' ').slice(1).join(' ')}
                </text>
              </>
            )}
          </g>

          {/* Valve Control */}
          <g id="valve-control" transform="translate(120, 55)" className="valve-control" data-testid="valve-preset">
            <PredefinedValvePlusThermostatValve
              d={thermostatOpening}
              temp={targetTemperature}
              value={Math.round(valvePosition * 7)}
              isHighlighted={showHighlights}
              onShowHighlightes={onShowHighlights}
              onStopAnimation={onStopAnimation}
              onChange={(v) => onValveChange && onValveChange(v / 7)}
              onTemperatureChange={onTemperatureChange}
              showFlowAnimation={true}
              flowRateLPM={flowRateLPM}
              isRunning={isRunning}
            />
          </g>

          {/* Highlight Overlay */}
          <g id="highlight-overlay">
            <circle
              cx={165}
              cy={305}
              r={30}
              strokeWidth={1}
              stroke="black"
              strokeDasharray="5"
              fill="transparent"
            />
            <line
              x1={165 + 30 * Math.cos(-Math.PI / 4)}
              x2={165 + 60 * Math.cos(-Math.PI / 4)}
              y1={305 + 30 * Math.sin(-Math.PI / 4)}
              y2={305 + 60 * Math.sin(-Math.PI / 4)}
              stroke="black"
              strokeWidth={1}
              strokeDasharray="5"
            />
          </g>

          <g id="highlight-circle" style={{ cursor: "zoom-in" }}>
            <circle
              cx={165}
              cy={305}
              r={30}
              fill="transparent"
              pointerEvents="all"
              style={{cursor:'zoom-in'}}
              onMouseEnter={() => onShowHighlights && onShowHighlights(true)}
              onMouseLeave={() => onShowHighlights && onShowHighlights(false)}
            />
          </g>

        </g>
      )}
    </svg>
  );
};

RoomUnit.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  roomTemperature: PropTypes.number,
  targetTemperature: PropTypes.number,
  radiatorCount: PropTypes.number,
  thermostatOpening: PropTypes.number,
  massFlowRate: PropTypes.number,
  valvePosition: PropTypes.number,
  onValveChange: PropTypes.func,
  onTemperatureChange: PropTypes.func,
  onShowHighlights: PropTypes.func,
  onStopAnimation: PropTypes.func,
  showHighlights: PropTypes.bool,
  isRunning: PropTypes.bool,
  showValveThermostat: PropTypes.bool,
  showRadiatorArea: PropTypes.bool,
};

RoomUnit.defaultProps = {
  roomTemperature: 15,
  targetTemperature: 20,
  radiatorCount: 5,
  thermostatOpening: 1,
  massFlowRate: 0,
  valvePosition: 0.5,
  showHighlights: false,
  isRunning: false,
  showValveThermostat: true,
  showRadiatorArea: true,
};

export default React.memo(RoomUnit);
